// MyProfilePage — the signed-in user's own account page (/my-profile), reached
// from the header account menu. Four stacked cards: an identity banner, a
// Details card that flips read <-> edit (name, email and app language are
// editable; phone is not), the Password card, and a read-only Membership card
// whose pill tabs show where the user sits in the company.
// Reads GET users/own-profile, saves POST users/update-own-profile.
//
// NOTE: `appLanguage` is a stored preference, NOT this portal's UI language —
// saving it must not touch i18n / localStorage.lang / the language slice. The
// header's EN-AR pill remains the only thing that changes the admin UI language.
//
// Every flow here is wired to a real endpoint: the profile save, the password
// change (ChangePasswordModal), and email verification end to end —
// VerifyEmailModal sends the code, VerifyEmailPage (/verify-email?code=)
// confirms it.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Field,
  Icon,
  Input,
  SelectDropdown,
  SegmentedControl,
} from '@/components/common';
import {
  useGetOwnProfileQuery,
  useUpdateOwnProfileMutation,
  type OwnProfile,
} from '@/api/endpoints/ownProfileApi';
import type { AppLanguage } from '@/redux/language/languageSlice';
import { setAuth } from '@/redux/auth/authSlice';
import { selectAuthUser } from '@/redux/auth/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import ChangePasswordModal from './ChangePasswordModal';
import VerifyEmailModal from './VerifyEmailModal';
import initialsOf from '@/utils/initials';
import { fmtDatePadded } from '@/utils/formatDate';

/** First letters of the first two words, e.g. "Umer Shahbaz" → "US". */

type MembershipTab = 'structure' | 'projects' | 'groups';

interface ProfileDraft {
  firstName: string;
  lastName: string;
  email: string;
  appLanguage: AppLanguage;
}

const emptyDraft: ProfileDraft = { firstName: '', lastName: '', email: '', appLanguage: 'en' };

const seedOf = (p: OwnProfile | undefined): ProfileDraft =>
  p
    ? { firstName: p.firstName, lastName: p.lastName, email: p.email, appLanguage: p.appLanguage }
    : emptyDraft;

/** Same rule (and message key) the auth pages use — see Signup.tsx. Email is
 *  optional here, so only a non-empty malformed value is an error. */
const emailSchema = yup.string().email('error_invalid_email');
const emailInvalid = (v: string) => !!v.trim() && !emailSchema.isValidSync(v.trim());

const MyProfilePage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const authUser = useAppSelector(selectAuthUser);

  const { data: profile, isFetching, isError, refetch } = useGetOwnProfileQuery();
  const [updateOwnProfile, { isLoading: saving }] = useUpdateOwnProfileMutation();

  const seed = useMemo(() => seedOf(profile), [profile]);
  const [draft, setDraft] = useState<ProfileDraft>(seed);
  // Re-seed when the query resolves (and after a save refetch).
  useEffect(() => setDraft(seed), [seed]);

  const [tab, setTab] = useState<MembershipTab>('structure');
  const [editing, setEditing] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const patch = (changes: Partial<ProfileDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const emailChanged = draft.email.trim() !== seed.email;
  const dirty =
    !!profile &&
    (draft.firstName !== seed.firstName ||
      draft.lastName !== seed.lastName ||
      emailChanged ||
      draft.appLanguage !== seed.appLanguage);
  const namesMissing = !draft.firstName.trim() || !draft.lastName.trim();
  const badEmail = emailInvalid(draft.email);
  const canSave = dirty && !namesMissing && !badEmail;

  const cancel = () => {
    setDraft(seed);
    setEditing(false);
  };

  const save = async () => {
    if (!canSave) return;
    const firstName = draft.firstName.trim();
    const lastName = draft.lastName.trim();
    const email = draft.email.trim();
    try {
      await updateOwnProfile({
        firstName,
        lastName,
        email: email || null,
        appLanguage: draft.appLanguage,
      }).unwrap();
      // The header reads identity from the auth slice, which only select-company
      // fills — patch it so a rename shows there without waiting for a refresh.
      if (authUser)
        dispatch(setAuth({ user: { ...authUser, firstName, lastName, email: email || null } }));
      toast.success(t('profile_saved'));
      setEditing(false);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Why Verify email is unavailable, as a message key — null when it is live.
  const verifyDisabledReason = !profile?.email
    ? 'profile_email_verify_no_email'
    : profile.emailVerified
      ? 'profile_email_verify_already'
      : null;

  const membership = profile?.membership;
  const languageLabel = t(
    draft.appLanguage === 'ar' ? 'profile_language_ar' : 'profile_language_en',
  );
  // The badge reads the same roles the Membership card lists, so the two can
  // never disagree — rather than a second source on the auth slice.
  const isOwner = !!membership?.roles.some((r) => r.isOwner);

  const tabs = [
    { value: 'structure', label: t('profile_tab_structure') },
    { value: 'projects', label: t('profile_tab_projects') },
    { value: 'groups', label: t('profile_tab_groups') },
  ];

  /** A chip row with its own empty state. The label is omitted on tabs that
   *  hold a single group, where it would just repeat the tab's own name. */
  const renderChipGroup = (label: string | null, emptyKey: string, chips: ReactNode[]) => (
    <div className='my-profile__group'>
      {label && <p className='my-profile__group-label'>{label}</p>}
      {chips.length > 0 ? (
        <div className='my-profile__chips'>{chips}</div>
      ) : (
        <p className='my-profile__empty'>{t(emptyKey)}</p>
      )}
    </div>
  );

  if (isFetching && !profile) {
    return (
      <div className='my-profile'>
        <div className='my-profile__status'>
          <span className='my-profile__spinner' aria-hidden='true' />
          <p className='my-profile__status-text'>{t('text_loading')}</p>
        </div>
      </div>
    );
  }

  // A failed fetch leaves `profile` undefined with `isFetching` false — without
  // this the page would render blank. Same idiom as FormBuilderPage's load error.
  if (isError || !profile || !membership) {
    return (
      <div className='my-profile'>
        <div className='my-profile__status'>
          <Icon name='circle-alert' size={28} />
          <p className='my-profile__status-text'>{t('profile_load_error')}</p>
          <Button variant='secondary' iconStart='rotate-cw' onClick={() => refetch()}>
            {t('forms_builder_retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='my-profile'>
      {/* ---- identity banner ---- */}
      <Card className='my-profile__banner'>
        <Avatar initials={initialsOf(profile.fullName)} size={56} />
        <span className='my-profile__identity'>
          <span className='my-profile__name'>{profile.fullName}</span>
          <span className='my-profile__email' dir='ltr'>
            {profile.email || '—'}
          </span>
        </span>
        {isOwner && (
          <Chip variant='neutral' iconStart='crown' className='my-profile__owner'>
            {t('profile_owner_badge')}
          </Chip>
        )}
      </Card>

      {/* ---- details ---- */}
      <Card
        padded={false}
        head={
          <div className='my-profile__card-head'>
            <span className='my-profile__card-title'>{t('profile_details_title')}</span>
            {!editing && (
              <Button
                variant='secondary'
                size='sm'
                iconStart='pencil'
                onClick={() => setEditing(true)}
              >
                {t('action_edit')}
              </Button>
            )}
          </div>
        }
      >
        <div className='my-profile__details'>
          {/* Read mode shows one combined Name row; editing splits it in two. */}
          {editing ? (
            <div className='my-profile__row'>
              <Field
                label={t('profile_first_name')}
                error={!draft.firstName.trim() ? t('profile_name_required') : undefined}
              >
                <Input
                  value={draft.firstName}
                  error={!draft.firstName.trim()}
                  onChange={(e) => patch({ firstName: e.target.value })}
                />
              </Field>
              <Field
                label={t('profile_last_name')}
                error={!draft.lastName.trim() ? t('profile_name_required') : undefined}
              >
                <Input
                  value={draft.lastName}
                  error={!draft.lastName.trim()}
                  onChange={(e) => patch({ lastName: e.target.value })}
                />
              </Field>
            </div>
          ) : (
            <div className='my-profile__static'>
              <p className='my-profile__label'>{t('profile_name')}</p>
              <p className='my-profile__value'>{profile.fullName || '—'}</p>
            </div>
          )}

          {/* Phone is set at invite time and cannot be edited here. */}
          <div className='my-profile__static'>
            <p className='my-profile__label'>{t('profile_phone')}</p>
            <p className='my-profile__value'>
              <span dir='ltr'>{profile.phone || '—'}</span>
              <Icon name='lock' size={14} className='my-profile__lock' />
            </p>
          </div>

          {editing ? (
            <Field
              label={t('profile_email')}
              htmlFor='profile-email'
              error={badEmail ? t('error_invalid_email') : undefined}
            >
              <Input
                id='profile-email'
                type='email'
                iconStart='mail'
                dir='ltr'
                value={draft.email}
                placeholder={t('profile_email_ph')}
                error={badEmail}
                onChange={(e) => patch({ email: e.target.value })}
              />
            </Field>
          ) : (
            <div className='my-profile__static'>
              <p className='my-profile__label'>{t('profile_email')}</p>
              <div className='my-profile__value my-profile__value--row'>
                <span dir='ltr'>{profile.email || '—'}</span>
                {!!profile.email &&
                  (profile.emailVerified ? (
                    <Chip variant='success' iconStart='circle-check'>
                      {t('profile_email_verified')}
                    </Chip>
                  ) : (
                    <Chip variant='warning' iconStart='circle-alert'>
                      {t('profile_email_unverified')}
                    </Chip>
                  ))}
                {/* Always present so the row keeps one shape; disabled when
                    there is nothing to verify. */}
                <Button
                  variant='secondary'
                  size='sm'
                  iconStart='mail'
                  className='my-profile__value-action'
                  disabled={!!verifyDisabledReason}
                  title={verifyDisabledReason ? t(verifyDisabledReason) : undefined}
                  onClick={() => setVerifyOpen(true)}
                >
                  {t('profile_email_verify')}
                </Button>
              </div>
              {/* Disabled buttons swallow pointer events, so `title` alone is
                  unreliable — say it on the page too. */}
              {verifyDisabledReason && (
                <p className='my-profile__hint'>{t(verifyDisabledReason)}</p>
              )}
            </div>
          )}

          {editing ? (
            <Field label={t('profile_language')} htmlFor='profile-language'>
              <SelectDropdown
                id='profile-language'
                size='lg'
                value={draft.appLanguage}
                onChange={(next) => patch({ appLanguage: next as AppLanguage })}
                options={[
                  { value: 'en', label: t('profile_language_en') },
                  { value: 'ar', label: t('profile_language_ar') },
                ]}
              />
            </Field>
          ) : (
            <div className='my-profile__static'>
              <p className='my-profile__label'>{t('profile_language')}</p>
              <p className='my-profile__value'>{languageLabel}</p>
            </div>
          )}
        </div>

        {/* The action bar belongs to edit mode — Cancel is what leaves it. */}
        {editing && (
          <div className='my-profile__footer'>
            {!dirty && (
              <span className='my-profile__saved'>
                <Icon name='check' size={15} />
                {t('guest_all_saved')}
              </span>
            )}
            <span className='my-profile__footer-actions'>
              <Button variant='secondary' onClick={cancel} disabled={saving}>
                {t('button_cancel')}
              </Button>
              <Button iconStart='check' onClick={save} loading={saving} disabled={!canSave}>
                {t('guest_save_changes')}
              </Button>
            </span>
          </div>
        )}
      </Card>

      {/* ---- password ---- */}
      <Card
        padded={false}
        head={
          <div className='my-profile__card-head'>
            <span className='my-profile__card-title'>{t('profile_password_title')}</span>
            <Button
              variant='secondary'
              size='sm'
              iconStart='pencil'
              onClick={() => setPasswordOpen(true)}
            >
              {t('action_edit')}
            </Button>
          </div>
        }
      >
        <div className='my-profile__card-body my-profile__static'>
          <p className='my-profile__label'>{t('profile_password_label')}</p>
          <p className='my-profile__mask' aria-hidden='true'>
            {'•'.repeat(12)}
          </p>
          <p className='my-profile__hint'>
            {t('profile_password_last_changed', {
              date: fmtDatePadded(profile.lastPasswordUpdateDate),
            })}
          </p>
        </div>
      </Card>

      {/* ---- membership (read-only) ---- */}
      <Card
        padded={false}
        head={
          <div className='my-profile__card-head'>
            <span className='my-profile__card-heading'>
              <span className='my-profile__card-title'>{t('profile_membership_title')}</span>
              <span className='my-profile__card-subtitle'>{t('profile_membership_subtitle')}</span>
            </span>
            <Chip variant='neutral' iconStart='eye'>
              {t('profile_read_only')}
            </Chip>
          </div>
        }
      >
        <div className='my-profile__membership'>
          <SegmentedControl
            variant='plain'
            options={tabs}
            value={tab}
            onChange={(next) => setTab(next as MembershipTab)}
            aria-label={t('profile_membership_title')}
          />

          {tab === 'structure' && (
            <>
              {renderChipGroup(
                t('profile_company_structure'),
                'profile_empty_structure',
                membership.nodes.map((n) => (
                  <span key={n.id} className='my-profile__place'>
                    <Icon name={n.levelIcon || 'building-2'} size={16} />
                    <span className='my-profile__place-name'>{n.name}</span>
                    <span className='my-profile__place-level'>{n.levelName}</span>
                  </span>
                )),
              )}
              {renderChipGroup(
                t('profile_roles'),
                'profile_empty_roles',
                membership.roles.map((r) => (
                  <Chip key={r.id} variant='neutral' iconStart='shield-check'>
                    {r.name}
                  </Chip>
                )),
              )}
            </>
          )}

          {tab === 'projects' &&
            renderChipGroup(
              null,
              'profile_empty_projects',
              membership.projects.map((p) => (
                <Chip key={p.id} variant='neutral' iconStart={p.icon || 'folder-kanban'}>
                  {p.title}
                </Chip>
              )),
            )}

          {tab === 'groups' &&
            renderChipGroup(
              null,
              'profile_empty_groups',
              membership.groups.map((g) => (
                <Chip key={g.id} variant='neutral' iconStart='users-round'>
                  {g.name}
                </Chip>
              )),
            )}
        </div>
      </Card>

      <ChangePasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      {/* Always the SAVED address. The entry button renders in read mode only,
          where the draft is by construction equal to the server value, so a
          stale address is unreachable rather than merely guarded against. */}
      <VerifyEmailModal
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        email={profile.email}
      />
    </div>
  );
};

export default MyProfilePage;
